import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import {
  buildEmptySubsection,
  buildEmptyTableRow,
  buildSummaryLines,
  computeRowLifetimeCost,
  computeSubsectionSubtotal,
  computeTotalLifetimeCost,
  isRowCostInputField,
  moveRowBetweenSubsections,
  planFieldPaths,
} from '../utils/lifecarePlanUtils';

// Formik orchestrator for the structured plan: owns the form state, exposes
// derived totals + add/remove handlers, and forwards every change to autosave.
const useLifecareStudioForm = ({ initialValues, onValuesChange, autosaveKey }) => {
  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;

  const formik = useFormik({
    initialValues,
    enableReinitialize: false,
    onSubmit: () => {},
  });

  const { values, setFieldValue, setValues, resetForm, dirty } = formik;
  const lastAutosaveKeyRef = useRef(autosaveKey);

  useEffect(() => {
    if (!autosaveKey) {
      lastAutosaveKeyRef.current = autosaveKey;
      return;
    }
    if (lastAutosaveKeyRef.current !== autosaveKey) {
      lastAutosaveKeyRef.current = autosaveKey;
      return;
    }
    if (!dirty) return;
    onValuesChangeRef.current?.(values);
  }, [values, autosaveKey, dirty]);

  // reinitialize the form when the active client changes (always safe, caller flushed
  // autosave) or when the server provides new content (AI edits, undo); skip server
  // updates while the user has unsaved local edits so typing is never overwritten
  const prevAutosaveKeyRef = useRef(autosaveKey);
  const prevInitialValuesRef = useRef(initialValues);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  // set to true by forceReinitOnNextUpdate so undo/redo can bypass the dirty guard once
  const forceReinitRef = useRef(false);
  useEffect(() => {
    const keyChanged = prevAutosaveKeyRef.current !== autosaveKey;
    const valuesChanged = prevInitialValuesRef.current !== initialValues;
    prevAutosaveKeyRef.current = autosaveKey;
    prevInitialValuesRef.current = initialValues;
    if (keyChanged) {
      forceReinitRef.current = false;
      resetForm({ values: initialValues });
      return;
    }
    if (!valuesChanged) return;
    if (!forceReinitRef.current && dirtyRef.current) return;
    forceReinitRef.current = false;
    resetForm({ values: initialValues });
  }, [autosaveKey, initialValues, resetForm]);

  // arm before an undo/redo so the next initialValues change resets regardless of dirty
  const forceReinitOnNextUpdate = useCallback(() => {
    forceReinitRef.current = true;
  }, []);

  // force-apply server content regardless of dirty state (e.g. undo triggered by user)
  const resetToServerValues = useCallback((nextValues) => {
    resetForm({ values: nextValues });
  }, [resetForm]);

  const handleAddSubsection = useCallback(() => {
    const currentSubsections = values?.futureCareNeeds?.subsections || [];
    const nextSubsections = [...currentSubsections, buildEmptySubsection()];
    setFieldValue(planFieldPaths.subsections, nextSubsections);
  }, [values, setFieldValue]);

  const handleRemoveSubsection = useCallback(
    ({ subsectionIndex }) => {
      const currentSubsections = values?.futureCareNeeds?.subsections || [];
      const nextSubsections = currentSubsections.filter((_, idx) => idx !== subsectionIndex);
      setFieldValue(planFieldPaths.subsections, nextSubsections);
    },
    [values, setFieldValue]
  );

  const handleAddRow = useCallback(
    ({ subsectionIndex }) => {
      const currentRows = values?.futureCareNeeds?.subsections?.[subsectionIndex]?.tables || [];
      const nextRows = [...currentRows, buildEmptyTableRow()];
      setFieldValue(planFieldPaths.subsectionTables(subsectionIndex), nextRows);
    },
    [values, setFieldValue]
  );

  const handleRemoveRow = useCallback(
    ({ subsectionIndex, rowIndex }) => {
      const currentRows = values?.futureCareNeeds?.subsections?.[subsectionIndex]?.tables || [];
      const nextRows = currentRows.filter((_, idx) => idx !== rowIndex);
      setFieldValue(planFieldPaths.subsectionTables(subsectionIndex), nextRows);
    },
    [values, setFieldValue]
  );

  // both subsections change at once, so we write the whole array in a single update
  const handleMoveRow = useCallback(
    ({ subsectionIndex, rowIndex, targetSubsectionIndex }) => {
      const currentSubsections = values?.futureCareNeeds?.subsections || [];
      const nextSubsections = moveRowBetweenSubsections({
        subsections: currentSubsections,
        subsectionIndex,
        rowIndex,
        targetSubsectionIndex,
      });
      if (!nextSubsections) return;
      setFieldValue(planFieldPaths.subsections, nextSubsections);
    },
    [values, setFieldValue]
  );

  // recompute the lifetimeCost cell when one of its inputs change. We keep this here (not in a
  // render-time formatter) so the saved row is always self-consistent. Editing an unrelated cell
  // (item, purpose, a comment) must not re-derive the cost: a row with no age span would have its
  // stored cost zeroed out just for a text tweak.
  const handleRowFieldChange = useCallback(
    ({ subsectionIndex, rowIndex, field, value }) => {
      const currentRow =
        values?.futureCareNeeds?.subsections?.[subsectionIndex]?.tables?.[rowIndex] ||
        buildEmptyTableRow();
      const nextRow = { ...currentRow, [field]: value };
      if (isRowCostInputField(field)) {
        nextRow.lifetimeCost = computeRowLifetimeCost(nextRow);
      }
      setFieldValue(planFieldPaths.tableRow(subsectionIndex, rowIndex), nextRow);
    },
    [values, setFieldValue]
  );

  const subsectionSubtotals = useMemo(() => {
    const subsections = values?.futureCareNeeds?.subsections || [];
    return subsections.map((subsection) => computeSubsectionSubtotal(subsection));
  }, [values]);

  const summaryLines = useMemo(() => buildSummaryLines(values), [values]);
  const totalLifetimeCost = useMemo(() => computeTotalLifetimeCost(values), [values]);

  const replaceValues = useCallback(
    (nextValues) => {
      setValues(nextValues, false);
    },
    [setValues]
  );

  return {
    formik,
    values,
    setFieldValue,
    replaceValues,
    resetToServerValues,
    forceReinitOnNextUpdate,
    subsectionSubtotals,
    summaryLines,
    totalLifetimeCost,
    handleAddSubsection,
    handleRemoveSubsection,
    handleAddRow,
    handleRemoveRow,
    handleMoveRow,
    handleRowFieldChange,
  };
};

export default useLifecareStudioForm;
